/**
 * TppAlertBulkModal Modal Modal component module.
 * @module advisor/components/modals/Notifications
 *
 */
import React from 'react';
import {connect} from 'react-redux';
import {Button, Column, DescriptiveText, Icon, LoadingSpinner, Row, Tabs, TextInput} from '../../../index';
import BaseModal from '../types/Base';
import {_gatherEntitlements, _openModal, _removeModal, isEntitled} from '../../../store';
import PropTypes from 'prop-types';
import moment from 'moment';
import debug from 'debug';
import {gatherData} from '../../../utils/utils';
import Table from '../../Table';
import axiosInstance from '../../../utils/axios';
import history from '../../../../history';
import useSWR from 'swr';

const log = debug('circleblack:advisor:modals:Notifications');
const gatherNotifications = gatherData('/user/notifications');

const BatchReportView = ({batchID, removeModal, modal, openModal}) => {
  const {data: batch} = useSWR(
    `/reports/batches/${batchID}`,
    url => axiosInstance.get(url)
      .then(res => {
        res.data.showRetry = res.data.statuses.some(status => status.code === 21);
        return res.data;
      })
  );
  const [drilledStatus, setDrilledStatus] = React.useState();
  const [columnName, setColumnName] = React.useState('Account');
  const dataManager = {
    gatherData: input => gatherData(`/reports/batches/${batchID}/${drilledStatus.code}/contexts`)(input).then(res => {
      let colName = 'Account';
      if (res && res[0] && res[0].email) {
        colName = 'Household';
      }
      if (colName !== columnName) {
        setColumnName(colName);
      }
      return res;
    }),
  };

  const householdEmailInput = (row, table) => <TextInput
    placeholder={'Household Email'}
    dataPoint={{name: 'email', value: row.email}}
    type={'email'}
    onChange={(name, value) => dataManager.setData(
      table.data.map(current => {
        if (current.id === row.id) {
          return {
            ...current,
            email: value,
          };
        }
        return current;
      })
    )}/>;

  if (!batch) {
    return <LoadingSpinner/>;
  }

  const openStatus = status => {
    // TODO Maybe more?
    setDrilledStatus(current => current && current.code === status.code ? null : status);
  };

  return <Column height={'400px'} layout={{grow: 1}}>
    <DescriptiveText>Report Generation Details</DescriptiveText>
    <Row justifySubRow={'space between'}>
      <div>{batch.statusTitle}</div>
      <div>{batch.completedAt}</div>
    </Row>
    <DescriptiveText>{batch.statusMessage}</DescriptiveText>
    {(batch.statusCode <= 6 || batch.showRetry) ? <Row>
      {batch.statusCode <= 6 && <Button importance={'secondary'}
                                        size={'md'}
                                        label={'Go To Reports List'}
                                        action={() => {
                                          removeModal(modal);
                                          return history.push('/report');
                                        }}/>}
      {batch.statusCode <= 6 && isEntitled([{group: 'advisor', feature: 'client_engagement', access: 'r'}])
      && <Button importance={'secondary'}
                 size={'md'}
                 label={'Go To Document Vault'}
                 action={() => {
                   removeModal(modal);
                   return history.push('/engage/documents');
                 }}/>}
      {batch.showRetry
      && <Button importance={'secondary'}
                 size={'md'}
                 label={'Retry Failed Reports'}
                 action={() => {
                   // TODO retry
                   removeModal(modal);
                   history.push(`/report?retryBatchID=${batchID}`);
                 }}/>}
    </Row> : null}
    <Column showDividers={true} height={'auto'}>
      {batch.statuses.map(status => <div key={`status-${status.code}`}>
        <DescriptiveText>{status.code >= 18
          ? <Icon svg={'sidebarChevron'} action={() => openStatus(status)}/>
          : null}{status.message}</DescriptiveText>
        {drilledStatus && drilledStatus.code === status.code
          ? (status.code === 18
            && <>
              <Button importance={'secondary'} size={'md'} label={'Update Emails and Retry Share'} action={() => {
                dataManager.setData(current => {
                  axiosInstance.put(`/reports/batches/${batchID}/email`, current.map(context => ({
                    id: context.householdID,
                    email: context.email,
                  })))
                    .then(() => {
                      setDrilledStatus(null);
                      openModal({
                        type: 'MessageModal',
                        title: 'Retry Share',
                        body: 'Successfully Shared',
                      });
                    })
                    .catch(err => openModal({type: 'Error', title: 'Retry Share Failed', err}));
                  return current;
                });
              }}/>
              <Table height={'200px'}
                     rowIdentifier={'householdID'}
                     defaultSort={{
                       column: 'name',
                       desc: false,
                     }}
                     dataManager={dataManager}
                     columns={[
                       {
                         title: 'Household',
                         dataPoint: 'name',
                         type: 'text',
                         minWidth: 80,
                         isSortable: true,
                       },
                       {
                         title: 'HOUSEHOLD EMAIL',
                         dataPoint: 'email',
                         type: 'custom',
                         render: householdEmailInput,
                         minWidth: 80,
                         isSortable: true,
                       },
                     ]}/></>
            || <Table height={'200px'}
                      defaultSort={{
                        column: 'name',
                        desc: false,
                      }}
                      dataManager={dataManager}
                      columns={[
                        {
                          title: columnName,
                          dataPoint: 'name',
                          type: 'text',
                          minWidth: 80,
                          isSortable: true,
                        },
                      ]}/>)
          : null}
      </div>)}
    </Column>
  </Column>;
};

BatchReportView.propTypes = {
  batchID: PropTypes.number.isRequired,
  removeModal: PropTypes.func.isRequired,
  modal: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
};

/**
 * Notifications
 * @name Notifications
 */
const Notifications = props => {
  const {content, removeModal, openModal} = props;

  const newNotificationsDataManager = {
    gatherData: search => gatherNotifications({...search, isRead: 0, sort: '-ts'}),
  };

  const [subContent, setSubContent] = React.useState();

  const pastNotificationsDataManager = {
    gatherData: search => gatherNotifications({...search, isRead: 1, sort: '-ts'}),
  };
  const openNotification = notification => {
    // notificationSelectedReadState = notification.isRead; Patch report modal needs to know if it was ready or not prior to clicked
    if (!notification.isRead) {
      notification.isRead = 1;
      // PotentiallyTODO Update counts
      // Master.unreadNotifications--;
      // if (!$scope.pastNotificationCount) {
      //   $scope.pastNotificationCount++;
      // }
      axiosInstance.put(`/notifications/${notification._id}/read`); // ETODO handle error
    }
    if (notification.data) {
      switch (notification.data.subtype) {
        case 'REPORT_BATCH':
          return setSubContent({
            backButtonLabel: 'Back to Notifications',
            content: <BatchReportView batchID={notification.data.id}
                                      modal={content}
                                      removeModal={removeModal}
                                      openModal={openModal}/>,
            rowID: notification._id,
            setRow: () => setSubContent(null),
          });
        case 'MODEL_UPDATE':
          if (!isEntitled([{group: 'models', feature: 'core', access: 'r'}])) {
            return openModal({type: 'MessageModal', title: 'No Access', body: 'You do not have access to models.'});
          }
          history.push('/models/list?isRecentlyUpdated=1');
          // TODO Check if this will refresh on same page otherwise need to do something to force update of data.
          return removeModal(content);
        default:
          return openModal({type: 'MessageModal', title: 'Notification', body: 'Notification marked as read'});
      }
    }
  };

  const infoIcon = () => <Icon svg={'details'}/>;

  const columns = React.useMemo(() => [
    {
      title: 'Notification',
      dataPoint: 'title',
      type: 'text',
      minWidth: 50,
      isSortable: false,
    },
    {
      title: 'Description',
      dataPoint: 'text',
      type: 'text',
      minWidth: 200,
      isSortable: false,
    },
    {
      title: 'Time',
      dataPoint: 'ts',
      type: 'datetime',
      minWidth: 50,
      isSortable: false,
      render: item => moment(item.ts).format('M/DD/YY h:mma'),
    },
    {
      title: 'View Details',
      dataPoint: 'id',
      type: 'custom',
      render: infoIcon,
      minWidth: 100,
      isSortable: false,
    },

  ].filter(c => !!c), []);

  log('rendering');
  return <BaseModal onClose={() => removeModal(content)} title={'Notifications'} size={'lg'}>
    <Tabs onChange={() => setSubContent(null)} tabs={[
      {
        title: 'New',
        content: <Table dataManager={newNotificationsDataManager}
                        columns={columns}
                        defaultSort={{
                          column: 'displayName',
                          desc: true,
                        }}
                        rowIdentifier={'_id'}
                        subContent={subContent}
                        rowOnClick={openNotification}
                        maxHeight={'calc(100vh - 200px)'}/>,
      },
      {
        title: 'Archived',
        content: <Table dataManager={pastNotificationsDataManager}
                        columns={columns}
                        defaultSort={{
                          column: 'displayName',
                          desc: true,
                        }}
                        rowIdentifier={'_id'}
                        subContent={subContent}
                        rowOnClick={openNotification}
                        maxHeight={'calc(100vh - 200px)'}/>,
      },
    ].filter(t => !!t)}
          displayTabTitles={true}
          minTabWidth={'150px'}
          tabWidth={'200px'}
          defaultSelection={0}/>
  </BaseModal>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  removeModal: modalToRemove => dispatch(_removeModal(modalToRemove)),
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  gatherEntitlements: refresh => dispatch(_gatherEntitlements(refresh)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);

Notifications.propTypes = {
  removeModal: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  content: PropTypes.object,
  session: PropTypes.object,
};
